import { useEffect, useState } from "react";
import { Button, Modal } from "semantic-ui-react";
import { FormError } from "../models/FormError";
import EditOrgUserForm, { FormData } from "./EditOrgUserForm";
import { CalibrationCertificate } from "../models/CalibrationCertificate";
import { trancateTime } from "../utils";
import { OrgUser_WithFilesAndAssignments } from "../models/OrgUser_WithFilesAndAssignments";
import { LicenceType, OrgUserFileInput } from "../models/globalTypes";
import AddOrgUserFileModalContainer from "../containers/AddOrgUserFileModalContainer";
import { OrgUserFile_AndInput } from "../models/OrgUserFile_AndInput";
import { ActiveLicence } from "../models/ActiveLicence";
import { AdminRole } from "../models/AdminRole";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    userId: string,
    firstName: string,
    lastName: string,
    adminRoleId: string | null,
    phoneNumber: string,
    licenceId: string | null,
    calibrationCertificate: CalibrationCertificate,
    newOrgUserFiles: OrgUserFileInput[],
    deletedOrgUserFileIds: string[],
  ) => void;
  submitInProgress: boolean;
  submitErrors?: string[];
  orgUser?: OrgUser_WithFilesAndAssignments;
  activeLicences?: ActiveLicence[] | null;
  canAssignAdminRole: boolean;
  adminRoles?: AdminRole[];
}

const EditOrgUserModal = ({
  open,
  onClose,
  onSubmit,
  submitInProgress,
  submitErrors,
  orgUser,
  activeLicences,
  canAssignAdminRole,
  adminRoles,
}: Props) => {
  const [formData, setFormData] = useState<FormData>({} as FormData);
  const [formErrors, setFormErrors] = useState<FormError>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [orgUserFiles, setOrgUserFiles] = useState<OrgUserFile_AndInput[]>([]);
  const [newOrgUserFiles, setNewOrgUserFiles] = useState<OrgUserFileInput[]>([]);
  const [openAddOrgUserFileModal, setOpenAddOrgUserFileModal] = useState(false);
  const [deletedOrgUserFileIds, setDeletedOrgUserFileIds] = useState<string[]>([]);
  const [isViewerLicenceSelected, setIsViewerLicenceSelected] = useState(false);

  useEffect(() => {
    if (open && orgUser) {
      setFormData({
        firstName: orgUser.user.firstName,
        lastName: orgUser.user.lastName,
        adminRoleId: orgUser.adminRole ? orgUser.adminRole.id : null,
        phoneNumber: orgUser.user.phoneNumber,
        CatSerialNumber: orgUser.calibrationCertificate?.catSerialNumber,
        CatCalibrationDate: orgUser.calibrationCertificate?.catCalibrationDate
          ? new Date(orgUser.calibrationCertificate.catCalibrationDate)
          : undefined,
        CatCalibrationDueDate: orgUser.calibrationCertificate?.catCalibrationDueDate
          ? new Date(orgUser.calibrationCertificate.catCalibrationDueDate)
          : undefined,
        GennySerialNumber: orgUser.calibrationCertificate?.gennySerialNumber,
        GennyCalibrationDate: orgUser.calibrationCertificate?.gennyCalibrationDate
          ? new Date(orgUser.calibrationCertificate.gennyCalibrationDate)
          : undefined,
        GennyCalibrationDueDate: orgUser.calibrationCertificate?.gennyCalibrationDueDate
          ? new Date(orgUser.calibrationCertificate.gennyCalibrationDueDate)
          : undefined,
        licenceId: orgUser.licenceId && String(orgUser.licenceId),
      } as FormData);
      setOrgUserFiles(orgUser.orgUserFiles);
      setIsViewerLicenceSelected(orgUser.licence?.type === LicenceType.VIEWER);
    } else {
      setFormData({} as FormData);
      setFormErrors({} as FormError);
      setHasSubmitted(false);
      setNewOrgUserFiles([]);
      setDeletedOrgUserFileIds([]);
    }
  }, [open, orgUser]);

  const validateFormData = (formData: FormData) => {
    const formError: FormError = {};

    if (!formData.firstName) {
      formError.firstName = "Please enter first name";
    }

    if (!formData.lastName) {
      formError.lastName = "Please enter last name";
    }

    return formError;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);

    const validationErrors = validateFormData(formData);
    setFormErrors(validationErrors);

    // If there is no validation error
    if (Object.keys(validationErrors).length === 0) {
      const {
        firstName,
        lastName,
        adminRoleId,
        phoneNumber,
        CatSerialNumber,
        CatCalibrationDate,
        CatCalibrationDueDate,
        GennySerialNumber,
        GennyCalibrationDate,
        GennyCalibrationDueDate,
        licenceId,
      } = formData;
      onSubmit(
        orgUser?.userId!,
        firstName,
        lastName,
        adminRoleId || null,
        phoneNumber,
        licenceId,
        {
          catSerialNumber: CatSerialNumber,
          catCalibrationDate: CatCalibrationDate ? trancateTime(CatCalibrationDate) : null,
          catCalibrationDueDate: CatCalibrationDueDate ? trancateTime(CatCalibrationDueDate) : null,
          gennySerialNumber: GennySerialNumber,
          gennyCalibrationDate: GennyCalibrationDate ? trancateTime(GennyCalibrationDate) : null,
          gennyCalibrationDueDate: GennyCalibrationDueDate ? trancateTime(GennyCalibrationDueDate) : null,
        } as CalibrationCertificate,
        newOrgUserFiles,
        deletedOrgUserFileIds,
      );
    }
  };

  const handleFormChange = (formData: FormData) => {
    if (hasSubmitted) {
      const validationErrors = validateFormData(formData);
      setFormErrors(validationErrors);
    }

    const viewerLicenceSelected =
      activeLicences?.find((l) => l.id === formData.licenceId)?.type === LicenceType.VIEWER;
    setIsViewerLicenceSelected(viewerLicenceSelected);
    if (viewerLicenceSelected) {
      formData.adminRoleId = null;
    }

    setFormData(formData);
  };

  const handleOrgUserFileAdded = (orgUserFile: OrgUserFileInput) => {
    setNewOrgUserFiles([...newOrgUserFiles, orgUserFile]);
    setOrgUserFiles([...orgUserFiles, orgUserFile]);
    setOpenAddOrgUserFileModal(false);
  };

  const handleDeleteOrgUserFile = (orgUserFile: OrgUserFile_AndInput) => {
    if (orgUserFile.id) {
      setDeletedOrgUserFileIds([...deletedOrgUserFileIds, orgUserFile.id]);
    } else {
      setNewOrgUserFiles([...newOrgUserFiles.filter((f) => f !== orgUserFile)]);
    }
    setOrgUserFiles([...orgUserFiles.filter((f) => f !== orgUserFile)]);
  };

  const handelClose = () => {
    setOrgUserFiles([]);
    setNewOrgUserFiles([]);
    setDeletedOrgUserFileIds([]);
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handelClose}>
        <Modal.Content>
          <Modal.Description>
            <EditOrgUserForm
              formErrors={formErrors}
              errors={submitErrors}
              formData={formData}
              onChange={handleFormChange}
              orgUserFiles={orgUserFiles}
              onAddOrgUserFile={() => setOpenAddOrgUserFileModal(true)}
              onDeleteOrgUserFile={handleDeleteOrgUserFile}
              activeLicences={activeLicences}
              licence={orgUser?.licence}
              licenceSelectorDisabled={false}
              adminRoles={adminRoles}
              adminRoleSelectorDisabled={!canAssignAdminRole || isViewerLicenceSelected}
            ></EditOrgUserForm>
          </Modal.Description>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handelClose}>Cancel</Button>
          <Button onClick={handleSubmit} positive loading={submitInProgress} disabled={submitInProgress}>
            Ok
          </Button>
        </Modal.Actions>
      </Modal>

      <AddOrgUserFileModalContainer
        open={openAddOrgUserFileModal}
        onOrgUserFileAdded={handleOrgUserFileAdded}
        onClose={() => setOpenAddOrgUserFileModal(false)}
      ></AddOrgUserFileModalContainer>
    </>
  );
};

export default EditOrgUserModal;
